import React, {useContext, useEffect, useRef, useState} from "react";
import useEditUser from "./useEditUser";
import {useParams} from "react-router-dom";
import AppbarActionButton from "../../../../commons/elements/buttons/AppbarActionButton";
import {Cancel, Save} from "@mui/icons-material";

import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import GeneralEditUserForm from "../utils/GeneralEditUserForm";
import useLoadUser from "../utils/useLoadUser";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import LastUpdateInfo from "../../../../commons/utils/lastUpdate/LastUpdateInfo";
import {useIntl} from "react-intl";
import {Box} from "@mui/material";
import {LockReset} from "@mui/icons-material";

const EditUserSection = () => {

    // ... the actual data is loaded based on the URL UID
    const {uid} = useParams()

    // Load the fresh user based on the uid from the URL parameter
    const {user, loading} = useLoadUser(uid)

    // Hook to update the appbar
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Hook providing the save user functionality
    const {saveUser, saveInProgress, cancel, resetPassword} = useEditUser()

    // Reference, which holds will hold the user data according to the current form data (from the general user component)
    const editedUserRef = useRef()

    // Determines if the save button should be enabled.
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false)

    // Internationalization for simple strings
    const intl = useIntl();

    const canPasswordBeReset = (providers) => {
        return providers.find((provider) => provider.providerId === 'password')
    }

    const getHeader = (user) => {
        if (!user) {
            return <Box width={200}/>
        }
        if (user.firstName && user.secondName) {
            return user.firstName + ' ' + user.secondName
        } else {
            return user.displayName
        }
    }

    // Setting the appbar content
    useEffect(() => {
        setCurrentAppbarContent({
            header: getHeader(user),
            description: <LastUpdateInfo metadata={user?.metadata} stopLoadingIndication={!!user}/>,
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.users.edit.appbar.actions.save',
                        defaultMessage: 'Save'
                    })}
                    onClick={() => saveUser(editedUserRef.current)}
                    icon={<Save/>}
                    disabled={!saveButtonEnabled}/>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.users.edit.appbar.actions.cancel',
                        defaultMessage: 'Cancel'
                    })}
                    onClick={() => cancel()}
                    icon={<Cancel/>}/>

                {user && canPasswordBeReset(user.providers) &&
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.users.edit.appbar.actions.resetPassword',
                        defaultMessage: 'Reset Password'
                    })}
                    onClick={() => resetPassword(editedUserRef.current)}
                    icon={<LockReset/>}/>}
            </>,
            progress: saveInProgress
        })
    }, [saveButtonEnabled, user, saveInProgress])

    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.users.edit.loading',
                defaultMessage: 'Loading user...'
            })}/>
    }

    if (saveInProgress) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.users.edit.save.loading',
                defaultMessage: 'Saving User...'
            })}/>
    }

    return (
        <GeneralEditUserForm selectedUser={user}
                             editedUserRef={editedUserRef}
                             setSaveButtonEnabled={setSaveButtonEnabled}/>
    );
}

export default EditUserSection